import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, ArrowLeft, Image as ImageIcon, Eye, Download, Trash2, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useNavigate } from 'react-router-dom';

interface Category {
  id: string;
  name: string;
  products: Product[];
}

interface Product {
  id: string;
  name: string;
  sku: string;
  aiImages: ImageFile[];
}

interface ImageFile {
  id: string;
  name: string;
  url: string;
  path: string;
  generatedAt: string;
  size: number;
}

interface MyAIModelsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MyAIModelsModal: React.FC<MyAIModelsModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentView, setCurrentView] = useState<'categories' | 'products' | 'images'>('categories');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null);
  const [pathHistory, setPathHistory] = useState<string[]>([]);

  // Cargar categorías y productos
  const loadCategories = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      console.log('🤖 Cargando categorías para modelos IA:', user.uid);
      
      // Usar el mismo servicio que el resto del sistema
      const optimizedService = (await import('../services/optimizedInventoryService')).default;
      const categoriesData = await optimizedService.getCategories();
      
      console.log(`📊 Categorías obtenidas del servicio: ${categoriesData.length}`);
      
      if (categoriesData.length === 0) {
        setError('No se encontraron categorías. Las categorías deben estar definidas en el sistema.');
        setLoading(false);
        return;
      }
      
      // Convertir las categorías del servicio al formato del modal
      // Mostrar todas las categorías (habilitadas y deshabilitadas)
      console.log(`📊 Mostrando todas las categorías: ${categoriesData.length}`);
      
      const categoriesForModal: Category[] = [];
      
      for (const category of categoriesData) {
        // Obtener productos de esta categoría desde Firestore
        const productsRef = window.auth.collection(window.auth.db, 'users', user.uid, 'categories', category.id, 'stock');
        const productsSnapshot = await window.auth.getDocs(productsRef);
        
        console.log(`📦 Productos en ${category.name}: ${productsSnapshot.docs.length}`);
        
        const products: Product[] = [];
        
        for (const productDoc of productsSnapshot.docs) {
          const productData = productDoc.data();
          const sku = productDoc.id;
          
          // Obtener imágenes IA de este producto desde Storage
          const aiImages = await loadProductAIImages(category.id, sku);
          
          products.push({
            id: sku,
            name: productData.name || `Producto ${sku}`,
            sku: sku,
            aiImages: aiImages
          });
        }
        
        categoriesForModal.push({
          id: category.id,
          name: category.name,
          products: products
        });
      }
      
      setCategories(categoriesForModal);
      console.log(`✅ Cargadas ${categoriesForModal.length} categorías para modelos IA`);
      
    } catch (error) {
      console.error('❌ Error cargando categorías para modelos IA:', error);
      setError('Error al cargar las categorías desde el servicio');
    } finally {
      setLoading(false);
    }
  };

  // Cargar imágenes IA de un producto específico
  const loadProductAIImages = async (categoryId: string, sku: string): Promise<ImageFile[]> => {
    if (!user || !window.auth?.storage) return [];

    try {
      console.log(`🤖 Cargando imágenes IA para ${categoryId}/${sku}`);
      
      // Intentar múltiples rutas posibles
      // La ruta principal es /imgIA que es donde se deben guardar las imágenes generadas con IA
      const possiblePaths = [
        `users/${user.uid}/categories/${categoryId}/stock/${sku}/imgIA`
      ];
      
      for (const aiImagesPath of possiblePaths) {
        try {
          console.log(`🔍 Intentando ruta IA: ${aiImagesPath}`);
          const aiImagesRef = window.auth.ref(window.auth.storage, aiImagesPath);
          const aiImagesSnapshot = await window.auth.listAll(aiImagesRef);
          
          console.log(`📁 Archivos IA encontrados en ${aiImagesPath}: ${aiImagesSnapshot.items.length}`);
          
          if (aiImagesSnapshot.items.length > 0) {
            const aiImages: ImageFile[] = [];
            
            for (const itemRef of aiImagesSnapshot.items) {
              const fileName = itemRef.name.toLowerCase();
              
              // Los archivos pueden no tener extensión, así que procesamos todos los archivos
              // y verificamos el tipo MIME después
              try {
                const url = await window.auth.getDownloadURL(itemRef);
                const metadata = await window.auth.getMetadata(itemRef);
                
                // Verificar si es una imagen por el tipo MIME
                const contentType = metadata.contentType || '';
                if (contentType.startsWith('image/')) {
                  // Si estamos en la carpeta /imgIA, todas las imágenes son generadas con IA
                  // No necesitamos filtrar por nombre
                  aiImages.push({
                    id: itemRef.name,
                    name: itemRef.name,
                    url: url,
                    path: itemRef.fullPath,
                    generatedAt: metadata.timeCreated || new Date().toISOString(),
                    size: metadata.size || 0
                  });
                  console.log(`✅ Imagen IA agregada: ${itemRef.name}`);
                }
              } catch (err) {
                console.warn('⚠️ Error obteniendo URL para imagen IA:', itemRef.name, err);
              }
            }
            
            console.log(`✅ ${aiImages.length} imágenes IA cargadas desde ${aiImagesPath}`);
            return aiImages;
          }
        } catch (error) {
          console.log(`⚠️ No se encontraron archivos IA en ${aiImagesPath}:`, error instanceof Error ? error.message : error);
        }
      }
      
      console.log(`❌ No se encontraron imágenes IA para ${categoryId}/${sku} en ninguna ruta`);
      return [];
      
    } catch (error) {
      console.error('❌ Error cargando imágenes IA:', error);
      return [];
    }
  };

  // Filtrar categorías por término de búsqueda
  const filteredCategories = categories.filter(category => {
    if (!searchTerm) return true;
    
    const categoryMatch = category.name.toLowerCase().includes(searchTerm.toLowerCase());
    const productMatch = category.products.some(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return categoryMatch || productMatch;
  });

  // Navegar a productos de una categoría
  const navigateToProducts = (category: Category) => {
    setSelectedCategory(category);
    setCurrentView('products');
    setPathHistory([...pathHistory, 'categories']);
  };

  // Navegar a imágenes IA de un producto
  const navigateToImages = (product: Product) => {
    setSelectedProduct(product);
    setCurrentView('images');
    setPathHistory([...pathHistory, 'products']);
  };

  // Volver atrás
  const goBack = () => {
    if (pathHistory.length === 0) {
      setCurrentView('categories');
      setSelectedCategory(null);
      setSelectedProduct(null);
    } else if (currentView === 'images') {
      setCurrentView('products');
      setSelectedProduct(null);
      const newHistory = [...pathHistory];
      newHistory.pop();
      setPathHistory(newHistory);
    } else if (currentView === 'products') {
      setCurrentView('categories');
      setSelectedCategory(null);
      const newHistory = [...pathHistory];
      newHistory.pop();
      setPathHistory(newHistory);
    }
  };

  // Abrir imagen en vista ampliada
  const openImagePreview = (image: ImageFile) => {
    setSelectedImage(image);
  };

  // Descargar imagen
  const downloadImage = async (image: ImageFile) => {
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = image.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('❌ Error descargando imagen IA:', error);
    }
  };

  // Eliminar imagen IA
  const deleteImage = async (image: ImageFile) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta imagen generada con IA?')) return;

    try {
      const imageRef = window.auth.storage.ref(image.path);
      await window.auth.storage.deleteObject(imageRef);
      
      // Recargar datos
      await loadCategories();
      
      // Si estamos viendo imágenes de un producto, recargar esas imágenes específicamente
      if (selectedProduct) {
        const updatedImages = await loadProductAIImages(selectedCategory!.id, selectedProduct.sku);
        setSelectedProduct({
          ...selectedProduct,
          aiImages: updatedImages
        });
      }
      
    } catch (error) {
      console.error('❌ Error eliminando imagen IA:', error);
      alert('Error al eliminar la imagen');
    }
  };

  // Ir a generar con IA
  const goToGenerateWithAI = () => {
    onClose();
    navigate('/generate-with-ai');
  };

  // Cargar datos al abrir el modal
  useEffect(() => {
    if (isOpen) {
      loadCategories();
      setCurrentView('categories');
      setSelectedCategory(null);
      setSelectedProduct(null);
      setSelectedImage(null);
      setPathHistory([]);
      setSearchTerm('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-4">
              {currentView !== 'categories' && (
                <button
                  onClick={goBack}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Mis Modelos IA
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {currentView === 'categories' && 'Selecciona una categoría'}
                  {currentView === 'products' && selectedCategory && `Productos en ${selectedCategory.name}`}
                  {currentView === 'images' && selectedProduct && `Modelos IA de ${selectedProduct.name}`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nombre de producto o SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {loading && (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                <span className="ml-2 text-gray-600 dark:text-gray-300">Cargando...</span>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-red-500 text-lg mb-2">❌</div>
                  <p className="text-red-600 dark:text-red-400">{error}</p>
                  <button
                    onClick={loadCategories}
                    className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    Reintentar
                  </button>
                </div>
              </div>
            )}

            {!loading && !error && (
              <div className="h-full overflow-y-auto p-6">
                {currentView === 'categories' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredCategories.map((category) => (
                      <motion.div
                        key={category.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => navigateToProducts(category)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                            <Sparkles className="w-6 h-6 text-purple-500" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 dark:text-gray-100">
                              {category.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {category.products.length} productos
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {currentView === 'products' && selectedCategory && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedCategory.products.map((product) => (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        onClick={() => navigateToImages(product)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                            <span className="text-green-500 text-lg">📦</span>
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 dark:text-gray-100">
                              {product.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              SKU: {product.sku}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {product.aiImages.length} modelos IA
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {currentView === 'images' && selectedProduct && (
                  <div>
                    {selectedProduct.aiImages.length === 0 ? (
                      <div className="text-center py-12">
                        <Sparkles className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                          No tenés imágenes generadas con IA
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6">
                          Genera imágenes con IA para este producto
                        </p>
                        <button
                          onClick={goToGenerateWithAI}
                          className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center space-x-2 mx-auto"
                        >
                          <Sparkles className="w-5 h-5" />
                          <span>Generar con IA</span>
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {selectedProduct.aiImages.map((image) => (
                          <motion.div
                            key={image.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="relative group bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden"
                          >
                            <div className="aspect-square relative">
                              <img
                                src={image.url}
                                alt={image.name}
                                className="w-full h-full object-cover"
                                onClick={() => openImagePreview(image)}
                              />
                              
                              {/* Badge de IA */}
                              <div className="absolute top-2 left-2 bg-purple-500 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1">
                                <Sparkles className="w-3 h-3" />
                                <span>IA</span>
                              </div>
                              
                              {/* Overlay de acciones */}
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openImagePreview(image);
                                    }}
                                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                  >
                                    <Eye className="w-4 h-4 text-gray-700" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadImage(image);
                                    }}
                                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                  >
                                    <Download className="w-4 h-4 text-gray-700" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteImage(image);
                                    }}
                                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </button>
                                </div>
                              </div>
                            </div>
                            
                            <div className="p-3">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                {image.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(image.generatedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Modal de vista ampliada de imagen */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-60 p-4"
              onClick={() => setSelectedImage(null)}
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                className="relative max-w-4xl max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={selectedImage.url}
                  alt={selectedImage.name}
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default MyAIModelsModal;
